/**
 * 대용량 첨부 업로드 파이프라인 — 설계 §5.1 uploadManager
 *
 * File.slice 읽기 → Worker 암호화 → 파트 presigned PUT(동시 3) → complete.
 *
 * 상주 메모리 (P4-a 리뷰 교훈 — 생산 지점만 보지 말고 소비 지점까지):
 *   File.slice 는 Blob(힙 밖)이고, arrayBuffer() 로 청크 1개만 힙에 올린 뒤 transfer 로
 *   Worker 에 넘겨 참조를 버린다. 암호문도 transfer 로 돌려받아 즉시 PUT 하고 버린다.
 *   → 힙 상주 = O(청크 × 동시 PUT 수) ≈ 8MiB × 3. 파일 크기와 무관하다.
 *
 * ⚠️ 의존 방향: 이 모듈은 Attachment/helper.js 를 **절대 import 하지 않는다** (설계 :200-202).
 */
import ResourceService from 'api/Resource'
import {
  DEFAULT_CHUNK_SIZE,
  IV_SIZE,
  MAX_CHUNK_SIZE,
  MIN_SAFE_CHUNK_SIZE,
  TAG_SIZE,
  ChunkFormatError,
  buildHeader,
  chunkCount,
} from './format'

const CHUNK_OVERHEAD = IV_SIZE + TAG_SIZE
/**
 * S3 멀티파트는 **마지막 파트를 제외한 모든 파트가 5MiB 이상**이어야 한다 (S3 제약).
 * 설계에는 이 제약이 적혀 있지 않지만, 청크 1개 = 파트 1개로 올리므로 chunkSize 가 작으면
 * complete 가 EntityTooSmall 로 실패한다. 기본값 8MiB 는 안전하고, 그보다 작은 값을 쓰려면
 * 청크 여러 개를 한 파트로 묶어야 하므로 여기서는 명시 거부한다.
 */
const MIN_S3_PART_SIZE = 5 * 1024 * 1024
const PART_CONCURRENCY = 3
const PART_MAX_ATTEMPTS = 3
const PART_RETRY_BASE_MS = 500
// 총 시간 상한이 아니라 **정지 감지** 창이다 — 8MiB 파트를 느린 회선으로 올리는 동안에도
// progress 이벤트가 오면 계속 연장된다. 60초 무진행이면 소켓이 죽은 것으로 본다.
const PART_STALL_TIMEOUT_MS = 60_000
// 백엔드 api/model/resource_multipart.py:25 의 MAX_PART_URLS_PER_REQUEST 미러 —
// 재발급 요청 1회당 서버가 주는 최대 개수
const MAX_PART_URLS_PER_REQUEST = 100
const SALT_SIZE = 16

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

/**
 * 파트 하나를 presigned URL 로 PUT 하고 ETag 를 돌려준다 (s3Upload.js:1-15 패턴 기반).
 *
 * s3Upload.js 와 달리 **정지 감지와 취소 연결**을 추가했다 (PR #1787 리뷰):
 * - `xhr.timeout`(총 시간 상한)은 쓰지 않는다 — 파트가 8MiB 라 느린 회선(1Mbps ≈ 65초)에서
 *   정상 업로드가 잘린다. 대신 `upload.onprogress` 로 **진행이 멈춘 시간**을 재 정지만 끊는다.
 * - `signal` 을 실제 xhr 에 연결한다. 재시도 경계에서만 검사하면 사용자가 취소해도 진행 중인
 *   PUT 이 끝까지 네트워크를 붙잡는다.
 */
const putPart = (url, body, signal, stallMs) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let stallTimer = null
    let settled = false

    const cleanup = () => {
      if (stallTimer !== null) clearTimeout(stallTimer)
      if (signal) signal.removeEventListener('abort', onAbort)
    }
    const settle = (fn, value) => {
      if (settled) return
      settled = true
      cleanup()
      fn(value)
    }
    function onAbort() {
      xhr.abort()
    }
    const armStallTimer = () => {
      if (stallTimer !== null) clearTimeout(stallTimer)
      stallTimer = setTimeout(() => {
        // 소켓이 멈추면 onload/onerror 어느 쪽도 오지 않아 promise 가 영구 pending 이 된다.
        // ⚠️ settle 을 abort 보다 **먼저** 한다 — abort 가 앞서면 onabort 가 ABORTED 로 선점해
        // 정지 원인이 가려지고, 취소는 재시도 대상이 아니라 전이적 정지도 재시도되지 않는다.
        settle(reject, new ChunkFormatError('PART_UPLOAD_STALLED', 'no upload progress'))
        xhr.abort()
      }, stallMs)
    }

    xhr.open('PUT', url)
    // 파트는 순수 바이트다 — content-type 을 붙이면 S3 서명 불일치가 날 수 있어 생략한다
    if (xhr.upload) {
      xhr.upload.onprogress = armStallTimer
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const rawEtag = xhr.getResponseHeader('etag') || ''
        const etag = rawEtag.replace(/"/g, '')
        if (!etag) {
          // CORS ExposeHeaders 에 ETag 가 없으면 여기서 빈 값이 된다 (설계 §6 의 ETag 함정)
          settle(reject, new ChunkFormatError('ETAG_UNAVAILABLE', 'S3 part ETag not readable'))
          return
        }
        settle(resolve, etag)
      } else {
        settle(
          reject,
          new ChunkFormatError('PART_UPLOAD_FAILED', `part PUT failed: HTTP ${xhr.status}`),
        )
      }
    }
    xhr.onerror = () =>
      settle(reject, new ChunkFormatError('PART_UPLOAD_FAILED', 'part PUT network error'))
    xhr.onabort = () => settle(reject, new ChunkFormatError('ABORTED', 'upload cancelled'))

    if (signal) {
      if (signal.aborted) {
        settle(reject, new ChunkFormatError('ABORTED', 'upload cancelled'))
        return
      }
      signal.addEventListener('abort', onAbort)
    }

    armStallTimer()
    xhr.send(body)
  })

/** 파트당 지수 백오프 재시도 (설계 :198 — 파트당 3회). */
const putPartWithRetry = async (url, body, signal, stallMs) => {
  let lastError = null
  for (let attempt = 1; attempt <= PART_MAX_ATTEMPTS; attempt += 1) {
    if (signal && signal.aborted) throw new ChunkFormatError('ABORTED', 'upload cancelled')
    try {
      // 순차 재시도가 목적
      // eslint-disable-next-line no-await-in-loop
      return await putPart(url, body, signal, stallMs)
    } catch (e) {
      lastError = e
      // ETag 부재는 CORS 설정 문제라 재시도해도 같다. 취소는 재시도 대상이 아니다.
      if (e.code === 'ETAG_UNAVAILABLE' || e.code === 'ABORTED') throw e
      if (attempt < PART_MAX_ATTEMPTS) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(PART_RETRY_BASE_MS * 2 ** (attempt - 1))
      }
    }
  }
  throw lastError
}

/**
 * 기본 Worker 생성자 — 테스트는 options.createWorker 로 fake 를 주입한다 (설계 :213).
 *
 * `import.meta.url` 을 쓰는 생성 코드는 cryptoWorkerFactory.js 에 격리하고 **동적 import** 로만
 * 가져온다 — jest 는 그 구문을 파싱조차 못 하므로(그 파일 주석 참조) 정적 import 를 두면
 * 이 모듈을 로드하는 모든 테스트가 죽는다.
 */
const resolveCreateWorker = async (injected) => {
  if (injected) return injected
  const mod = await import('./cryptoWorkerFactory')
  return mod.default
}

/**
 * Worker 를 요청/응답 1:1 로 감싼다. 암호화는 단일 Worker 에서 **순차** 수행한다 —
 * 병렬화하면 상주 메모리가 청크 수에 비례해 늘어난다.
 */
const createEncryptor = (createWorker) => {
  const worker = createWorker()
  let seq = 0
  const pending = new Map()

  const settleAll = (error) => {
    pending.forEach(({ reject }) => reject(error))
    pending.clear()
  }

  worker.onmessage = (event) => {
    const data = event.data || {}
    if (data.type === 'ready') {
      const entry = pending.get('init')
      if (entry) {
        pending.delete('init')
        entry.resolve()
      }
      return
    }
    if (data.type === 'encrypted') {
      const entry = pending.get(data.chunkIndex)
      if (entry) {
        pending.delete(data.chunkIndex)
        entry.resolve(data.record)
      }
      return
    }
    if (data.type === 'error') {
      const err = new ChunkFormatError(data.code, `worker ${data.code} at chunk ${data.chunkIndex}`)
      const entry = pending.get(data.chunkIndex === -1 ? 'init' : data.chunkIndex)
      if (entry) {
        pending.delete(data.chunkIndex === -1 ? 'init' : data.chunkIndex)
        entry.reject(err)
      } else {
        settleAll(err)
      }
    }
  }

  // worker.onerror 병행 수신 (설계 :207) — postMessage 로 못 올라오는 치명 오류 대비
  worker.onerror = () => {
    settleAll(new ChunkFormatError('ENCRYPT_FAILED', 'worker crashed'))
  }

  return {
    init: (password, salt, chunkSize, totalSize) =>
      new Promise((resolve, reject) => {
        pending.set('init', { resolve, reject })
        worker.postMessage({ type: 'init', password, salt, chunkSize, totalSize })
      }),
    encrypt: (chunkIndex, plainBuffer) =>
      new Promise((resolve, reject) => {
        pending.set(chunkIndex, { resolve, reject })
        seq += 1
        worker.postMessage({ type: 'encrypt', chunkIndex, plain: plainBuffer }, [plainBuffer])
      }),
    // 에러 시 재생성이 원칙이므로(설계 :207) 종료는 호출부가 명시적으로 한다
    terminate: () => {
      settleAll(new ChunkFormatError('ABORTED', 'encryptor terminated'))
      worker.terminate()
    },
    get requestCount() {
      return seq
    },
  }
}

const assertChunkSize = (chunkSize, totalChunks) => {
  if (chunkSize < MIN_SAFE_CHUNK_SIZE || chunkSize > MAX_CHUNK_SIZE) {
    throw new ChunkFormatError(
      'UNSAFE_CHUNK_SIZE',
      `chunkSize ${chunkSize} outside [${MIN_SAFE_CHUNK_SIZE}, ${MAX_CHUNK_SIZE}]`,
    )
  }
  if (totalChunks > 1 && chunkSize + CHUNK_OVERHEAD < MIN_S3_PART_SIZE) {
    throw new ChunkFormatError(
      'PART_TOO_SMALL',
      `part size ${chunkSize + CHUNK_OVERHEAD} < S3 minimum ${MIN_S3_PART_SIZE}`,
    )
  }
}

/**
 * 파일 하나를 신포맷으로 암호화해 S3 멀티파트로 올린다.
 *
 * @param {File|Blob} file 평문 파일
 * @param {object} params
 * @param {string} params.resourceId 대상 리소스 id (URL 경로에 들어간다)
 * @param {string} params.password 키 재료 = rsKeyObj.rsKey (바이트 문자열)
 * @param {string} params.filename 서버에 신고할 파일명
 * @param {string} params.contentType 서버에 신고할 MIME
 * @param {object} [options]
 * @param {number} [options.chunkSize] 평문 청크 크기 (기본 8MiB)
 * @param {(p:{phase:string, bytes:number, totalBytes:number, ratio:number}) => void} [options.onProgress]
 * @param {AbortSignal} [options.signal]
 * @param {() => Worker} [options.createWorker] 테스트용 fake worker 주입
 * @returns {Promise<{object_key:string, etag:string, size:number, content_type:string}>}
 *   기존 파일 필드 계약과 동일한 키 (설계 :203-204)
 */
export const uploadChunked = async (file, params, options = {}) => {
  const { resourceId, password, filename, contentType } = params
  const {
    chunkSize = DEFAULT_CHUNK_SIZE,
    onProgress,
    signal,
    createWorker,
    // 정지 감지 창 — 테스트가 60초를 기다리지 않도록 주입 가능하게 둔다
    stallTimeoutMs = PART_STALL_TIMEOUT_MS,
  } = options

  const totalSize = file.size
  const totalChunks = chunkCount(totalSize, chunkSize)
  assertChunkSize(chunkSize, totalChunks)

  const throwIfAborted = () => {
    if (signal && signal.aborted) throw new ChunkFormatError('ABORTED', 'upload cancelled')
  }
  const report = (phase, bytes) => {
    if (!onProgress) return
    try {
      onProgress({
        phase,
        bytes,
        totalBytes: totalSize,
        ratio: totalSize === 0 ? 1 : bytes / totalSize,
      })
    } catch (e) {
      // 진행률 표시 실패가 업로드를 막아선 안 된다 (downloadManager 와 동일 방침)
    }
  }

  throwIfAborted()
  const initiated = await ResourceService.initiateMultipart(resourceId, {
    filename,
    size: totalSize,
    content_type: contentType,
  })
  const { upload_id: uploadId, object_key: objectKey, part_urls: initialPartUrls } = initiated.data

  const workerFactory = await resolveCreateWorker(createWorker)
  const salt = crypto.getRandomValues(new Uint8Array(SALT_SIZE))
  const header = buildHeader({ chunkSize, totalSize, salt })

  const urlByPart = new Map()
  initialPartUrls.forEach((p) => urlByPart.set(p.part_number, p.url))

  /**
   * 파트 URL 이 아직 없거나 만료됐으면 이어서 발급받는다 (설계 :279 재발급 API).
   *
   * **배치로 받는다** (PR #1787 리뷰): 파트당 count:1 로 받으면 재발급 경로에서 파트 수만큼
   * 순차 왕복이 업로드를 막는다. 백엔드가 요청당 100개까지 주므로(MAX_PART_URLS_PER_REQUEST)
   * 남은 파트 수만큼(상한 100) 한 번에 받아 캐시한다.
   */
  const urlForPart = async (partNumber) => {
    if (!urlByPart.has(partNumber)) {
      const remaining = totalChunks - partNumber + 1
      const more = await ResourceService.issueMultipartPartUrls(resourceId, {
        object_key: objectKey,
        upload_id: uploadId,
        from_part: partNumber,
        count: Math.min(remaining, MAX_PART_URLS_PER_REQUEST),
      })
      more.data.part_urls.forEach((p) => urlByPart.set(p.part_number, p.url))
    }
    return urlByPart.get(partNumber)
  }

  const etags = []
  let uploadedPlainBytes = 0
  const inFlight = new Set()

  // 루프 밖에서 정의한다 — 루프 안에서 만들면 eslint no-loop-func 이 가변 바인딩(uploadedPlainBytes)
  // 캡처를 위험으로 잡는다. 파트별 값은 인자로 받아 클로저 캡처를 없앤다.
  const onPartUploaded = (partNumber, plainLen) => (etag) => {
    etags.push({ part_number: partNumber, etag })
    uploadedPlainBytes += plainLen
    report('uploading', uploadedPlainBytes)
  }

  /**
   * 첫 실패를 따로 붙잡는다.
   *
   * ⚠️ 정리 콜백이 실패한 task 를 inFlight 에서 지우므로, 실패가 **루프 진행 중**에 확정되면
   * 마지막 `Promise.all(inFlight)` 이 그 거부를 보지 못한다 → 파트가 빠진 채 complete 되어
   * 사용자는 실제 원인(예: ETag 부재) 대신 서버의 크기 불일치 에러를 본다. 실제로 재현됐고
   * 회귀 테스트("먼저 올린 파트가 실패하면 …")로 고정했다.
   */
  let firstFailure = null
  const rememberFailure = (e) => {
    if (firstFailure === null) firstFailure = e
  }
  const throwIfPartFailed = () => {
    if (firstFailure !== null) throw firstFailure
  }

  // Worker 생성을 try 안으로 넣는다 (PR #1787 리뷰) — 생성 후 try 진입 전에 throw 되면
  // finally 를 못 타 Worker 가 누수된다.
  let encryptor = null
  try {
    encryptor = createEncryptor(workerFactory)
    await encryptor.init(password, salt, chunkSize, totalSize)

    for (let i = 0; i < totalChunks; i += 1) {
      throwIfAborted()
      throwIfPartFailed()

      const start = i * chunkSize
      const end = Math.min(start + chunkSize, totalSize)
      // Blob.slice 는 힙에 복사하지 않는다 — arrayBuffer() 시점에만 청크 1개가 힙에 올라온다
      // eslint-disable-next-line no-await-in-loop
      const plainBuffer = await file.slice(start, end).arrayBuffer()
      const plainLen = plainBuffer.byteLength

      // 암호화는 단일 Worker 순차 — transfer 로 넘기므로 이 시점에 힙 참조가 사라진다
      // eslint-disable-next-line no-await-in-loop
      const record = await encryptor.encrypt(i, plainBuffer)

      const partNumber = i + 1
      // 헤더(36B)는 객체 선두여야 하므로 1번 파트 앞에 붙인다
      const body = partNumber === 1 ? new Blob([header, record]) : new Blob([record])

      // eslint-disable-next-line no-await-in-loop
      const url = await urlForPart(partNumber)

      const task = putPartWithRetry(url, body, signal, stallTimeoutMs).then(
        onPartUploaded(partNumber, plainLen),
      )
      inFlight.add(task)
      // ⚠️ `task.finally(cleanup)` 은 **새 promise** 를 만들고, task 가 거부되면 그 새 promise 도
      // 거부되는데 아무도 처리하지 않아 unhandled rejection 으로 프로세스가 죽는다(실제로 겪음).
      // 두 인자 then 은 양쪽을 다 처리해 새 promise 가 항상 이행되고, 동시에 task 에 핸들러가
      // 붙어 "미처리" 표시도 사라진다. 실제 에러 전파는 아래 Promise.race/all 이 담당한다.
      task.then(
        () => inFlight.delete(task),
        (e) => {
          inFlight.delete(task)
          rememberFailure(e)
        },
      )

      if (inFlight.size >= PART_CONCURRENCY) {
        // 동시 PUT 수를 묶어 상주 메모리를 O(청크 × 3) 으로 제한한다
        // eslint-disable-next-line no-await-in-loop
        await Promise.race(inFlight)
      }
    }

    await Promise.all(inFlight)
    throwIfAborted()
    throwIfPartFailed()

    // 파트 수가 청크 수와 맞지 않으면 complete 하지 않는다 — 위 가드가 놓친 경로가 있어도
    // 파트가 빠진 객체를 서버에 확정시키지 않는 최종 방어선
    if (etags.length !== totalChunks) {
      throw new ChunkFormatError(
        'PART_COUNT_MISMATCH',
        `uploaded ${etags.length} parts, expected ${totalChunks}`,
      )
    }

    // complete 는 파트를 오름차순으로 요구한다 (백엔드 모델 validate_parts 도 정렬을 강제)
    etags.sort((a, b) => a.part_number - b.part_number)
    report('completing', totalSize)

    const completed = await ResourceService.completeMultipart(resourceId, {
      object_key: objectKey,
      upload_id: uploadId,
      declared_size: totalSize,
      chunk_size: chunkSize,
      parts: etags,
    })

    return {
      object_key: completed.data.object_key,
      etag: completed.data.etag,
      // size 는 **평문** 크기다. 리소스 저장 body 에 넣어야 하는 값은 이게 아니라 object_size 다 —
      // 백엔드 _validate_s3_files 가 파일 dict 의 size 를 S3 실측 content_length 와 대조해
      // 다르면 SAFEBOX_853_FILE_SIZE_MISMATCH 를 던진다(api/resource_presigned.py). SBC1 객체는
      // 헤더 36 + 청크당 28 만큼 평문보다 크므로 평문 크기를 넣으면 저장이 항상 실패한다.
      size: totalSize,
      object_size: completed.data.object_size,
      content_type: contentType,
    }
  } catch (e) {
    // 실패·취소 시 서버에 abort 를 알려 파트를 즉시 정리한다.
    // 실패해도 삼킨다 — 버킷 lifecycle(abortIncompleteMultipartUpload 7일, PR #1780)이 최종 안전망.
    try {
      await ResourceService.abortMultipart(resourceId, {
        object_key: objectKey,
        upload_id: uploadId,
      })
    } catch (abortError) {
      // 원래 에러를 덮지 않는다
    }
    throw e
  } finally {
    if (encryptor !== null) encryptor.terminate()
  }
}

/**
 * 업로드 중 페이지 이탈 경고를 붙인다 (설계 :217).
 *
 * **재개는 지원하지 않는다** — 진행 상태는 메모리에만 있어 새로고침하면 항목이 사라지고,
 * 남은 S3 파트는 버킷 lifecycle(7일)이 정리한다. 그래서 이탈 전에 경고만 준다.
 * @returns {() => void} 해제 함수
 */
export const guardUnloadDuringUpload = () => {
  if (typeof window === 'undefined') return () => {}
  const handler = (event) => {
    event.preventDefault()
    // 최신 브라우저는 커스텀 문구를 무시하고 기본 문구를 쓴다 (설계 §5.3 이 키 불필요로 판단)
    event.returnValue = ''
    return ''
  }
  window.addEventListener('beforeunload', handler)
  return () => window.removeEventListener('beforeunload', handler)
}
